import { FC, useCallback, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useAppStore } from "@/store";
import { GettingStartedCard } from "./getting-started-card";
import { GettingStartedCardStep } from "./getting-started-card.types";
import styled from "@emotion/styled";
import { GettingStartedHelpModal } from "./getting-started-help-modal";
import type { StepId } from "@/store/getting-started";
import { windowModule } from "@/modules/window";

/**
 * Container component that connects GettingStartedCard to AppStoreGettingStartedStore.
 * Handles all data transformation and callback logic.
 */
export const GettingStartedCardContainer: FC = observer(function GettingStartedCardContainer() {
  const { store } = useAppStore();
  const gettingStartedStore = store.gettingStarted;
  const [helpModalStepId, setHelpModalStepId] = useState<StepId | null>(null);

  // Mark that user has seen the getting started card when component mounts
  useEffect(() => {
    gettingStartedStore.markGettingStartedCardSeen();
  }, [gettingStartedStore]);

  const handleHelpClick = useCallback((stepId: string) => {
    setHelpModalStepId(stepId as StepId);
  }, []);

  const handleCloseHelpModal = useCallback(() => {
    setHelpModalStepId(null);
  }, []);

  const handleExploreMoreClick = useCallback(() => {
    void windowModule.openInNewTab({ url: new URL("https://help.mem.ai/") });
  }, []);

  // Transform store steps to component steps format
  // No useMemo needed - observer will track MobX observables and re-render automatically
  const steps: GettingStartedCardStep[] = gettingStartedStore.steps.map(step => {
    const config = gettingStartedStore.getStepConfig(step.id);
    return {
      id: step.id,
      title: step.title,
      isCompleted: !!step.completedAt,
      shouldShowHelp: !!config?.helpContent,
      tooltip: config?.tooltip,
    };
  });

  // Show onboarding tooltip based on store state
  const shouldShowOnboardingTooltip = gettingStartedStore.isOnboardingTooltipVisible;

  return (
    <StyledContainer>
      <GettingStartedCard
        steps={steps}
        isAllComplete={gettingStartedStore.isComplete}
        progress={Math.round(gettingStartedStore.progress * 100)}
        completedCount={gettingStartedStore.completedCount}
        totalCount={gettingStartedStore.totalCount}
        initiallyCollapsed={gettingStartedStore.isCollapsed}
        hasShownConfetti={gettingStartedStore.hasShownConfetti}
        onboardingTooltip={
          shouldShowOnboardingTooltip
            ? {
                title: "Start here",
                subtitle: "Learn the basics of capturing and organizing notes in Mem",
                isOpen: true,
                onDismiss: gettingStartedStore.dismissOnboardingTooltip,
              }
            : undefined
        }
        onHelpClick={handleHelpClick}
        onToggleExpanded={gettingStartedStore.toggleGuide}
        onExploreMoreClick={handleExploreMoreClick}
        onDismiss={gettingStartedStore.dismissGuide}
        onConfettiShown={gettingStartedStore.markConfettiShown}
      />
      {helpModalStepId && (
        <GettingStartedHelpModal
          isOpen={true}
          stepId={helpModalStepId}
          onClose={handleCloseHelpModal}
        />
      )}
    </StyledContainer>
  );
});

const StyledContainer = styled.div`
  margin-bottom: 45px;
`;
